import moment from 'moment-timezone';
import { ElLoading } from 'element-plus';


/**
 * Shortens a label to a specified length and appends an additional string if it exceeds a minimum length.
 *
 * @param {string} label - The original label to be shortened.
 * @param {number} setLength - The length to which the label should be shortened.
 * @param {number} setMinLength - The minimum length the label must exceed to be shortened.
 * @param {string} [addOnLabel=""] - The string to append to the shortened label.
 * @returns {string} - The shortened label with the appended string, or the original label if it does not exceed the minimum length.
 */
export function shorterLabel(label="", setLength, setMinLength, addOnLabel="") {
  try {
    if (label && label.length > setMinLength) {
        return label.substring(0, setLength) + addOnLabel;
    }
    return label;
  } catch (error) {
    console.error('Error in shorterLabel:', error);
    return;
  }
}


/** convert response data from UTC to AU/SYD 
 * Converts a given date string to a specified format in the 'Australia/Sydney' timezone.
 *
 * @param {string} dateString - The date string to be converted. Expected format: 'YYYY-MM-DDTHH:mm:ss.sssZ'.
 * @param {string} type - The type of format to return. Can be 'date', 'time', or 'date_time'.
 * @returns {string} - The formatted date string in the 'Australia/Sydney' timezone.
 *
 * Description:
 * - The function first cleans the input date string by removing any fractional seconds and appending 'Z' to indicate UTC time.
 * - It then creates a moment object in UTC and converts it to the 'Australia/Sydney' timezone.
 * - Depending on the 'type' parameter, the function returns the date in one of the following formats:
 *   - 'date': 'YYYY-MM-DD'
 *   - 'time': 'HH:mm:ss'
 *   - 'date_time': 'YYYY-MM-DD HH:mm:ss'
 */
export function convertTimezone(dateString, type) {
        const cleanedDateString = dateString.split('.')[0] + 'Z';
        const date = moment.utc(cleanedDateString).tz('Australia/Sydney');
        if (type === 'date') {
          return date.format('YYYY-MM-DD');
        } else if (type === 'time') {
          return date.format('HH:mm:ss');
        }
        if (type === 'date_time') {
          return date.format('YYYY-MM-DD HH:mm:ss');
        }
    }


/**
 * Groups an array of objects by a specified key.
 *
 * @param {Array} array - The array to be grouped.
 * @param {Function} keyGetter - A function that returns the key to group by.
 * @returns {Object} - An object where the keys are the group keys and the values are arrays of grouped items.
 */
export function groupBy(array, keyGetter) {
      const map = new Map();
      array.forEach((item) => {
          const key = keyGetter(item);
          const collection = map.get(key);
          if (!collection) {
              map.set(key, [item]);
          } else {
              collection.push(item);
          }
      });
      return Object.fromEntries(map);
  }

/**
 * Converts a string from snake_case to Title Case or applies specific transformations based on options.
 *
 * @param {string} str - The string to be converted.
 * @param {boolean} uppercase - If true, converts the entire string to uppercase.
 * @param {boolean} keepUnderscore - If true, keeps underscores in the string and converts to Title Case.
 * @returns {string} - The converted string based on the options.
 */
export function convertToTitleCase(str, uppercase = false, keepUnderscore = false) {
  if (uppercase) {
    return str.toUpperCase();
  }

  if (keepUnderscore) {
    return str
      .split('_')
      .map(word => {
        if (word.toLowerCase() === 'and') {
          return 'and';
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join('_');
  }

  return str
    .split('_')
    .map(word => {
      if (word.toLowerCase() === 'and') {
        return 'and';
      }
      if (word.toLowerCase() === 'of') {
        return 'of';
      }
      if (word.toLowerCase() === 'per') {
        return 'per';
      }
      if (word.toLowerCase() === 'tco2') {
        return 'tCO2';
      }
      if (word.toLowerCase() === 'afec') {
        return 'AFEC';
      }
      if (word.toLowerCase() === 'mpg') {
        return 'MPG';
      }
      if (word.toLowerCase() === 'no') {
        return 'No.';
      }
      if (word.toLowerCase() === 'ec') {
        return 'EC';
      }
      if (word.toLowerCase() === 'ed') {
        return 'ED';
      }
      if (word.toLowerCase() === 'lec') {
        return 'LEC';
      }
      if (word.toLowerCase() === 'ecb') {
        return 'ECB';
      }
      if (word.toLowerCase() === 'er') {
        return 'ER';
      }
      if (word.toLowerCase() === 'le') {
        return 'LE';
      }
      if (word.toLowerCase() === 'nect') {
        return 'NECT';
      }
      if (word.toLowerCase() === 'ef') {
        return 'EF';
      }
      if (word.toLowerCase() === 'pe') {
        return 'PE';
      }
      if (word.toLowerCase() === 'lect') {
        return 'LECT';
      }
      if (word.toLowerCase() === 'ev') {
        return 'EV';
      }
      if (word.toLowerCase() === 'ir') {
        return 'IR';
      }
      if (word.toLowerCase() === 'instanceid') {
        return 'Instance ID';
      }
      if (word.toLowerCase() === 'tod') {
        return 'tod';
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

// Example usage:
// console.log(convertToTitleCase("north_scotland")); // Output: "North Scotland"
// console.log(convertToTitleCase("nsw", true)); // Output: "NSW"
// console.log(convertToTitleCase("north_scotland", false, true)); // Output: "North_Scotland"
// console.log(convertToTitleCase("north_and_island", false, true)); // Output: "North_and_Island"
// console.log(convertToTitleCase("north_and_island")); // Output: "North and Island"

/**
 * Converts a base64 string to a PDF file and download it.
 *
 * @param {string} base64 - The base64 string to be converted.
 * @returns {string} - The URL of the PDF file.
 */
export function convertBase64ToPDF(base64, filename) {

  try {
    // Remove data URL prefix if present
    const base64Data = base64.replace(/^data:application\/pdf;base64,/, '');
    
    // Convert base64 to binary
    const binaryString = window.atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // Create blob and download
    const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = filename || 'document.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(pdfUrl);
  } catch (error) {
    console.error('Error in convertBase64ToPDF:', error);
    throw error;
  }
}

/**
 * Creates a loading instance with custom spinner and background configuration.
 * 
 * @param {string|null} text - Optional text to display with the loading spinner
 * @returns {Object} - ElLoading service instance with custom configuration
 */
export function createLoadingInstance(text = null, opacity = 0.9) {
  const config = {
    lock: true,
    background: `rgba(13, 57, 55, ${opacity})`,
    spinner: `<svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
       <path class="spinner-outer" d="M42.4483 26.5718C42.6964 26.5919 42.9143 26.4069 42.9282 26.1584C43.0955 23.149 42.5068 20.143 41.2125 17.4157C39.8538 14.5526 37.7653 12.0975 35.157 10.2974C32.5488 8.4972 29.5126 7.41531 26.3537 7.16044C23.1948 6.90558 20.0244 7.48672 17.1613 8.84546C14.2982 10.2042 11.8431 12.2927 10.043 14.9009C8.2428 17.5092 7.16091 20.5454 6.90605 23.7043C6.65118 26.8632 7.23233 30.0335 8.59106 32.8967C9.88533 35.6239 11.8417 37.9809 14.2788 39.7543C14.4801 39.9008 14.7611 39.8489 14.9025 39.644V39.644C15.0439 39.4392 14.9921 39.1589 14.791 39.0122C12.4841 37.3288 10.6319 35.0945 9.40545 32.5102C8.11455 29.79 7.56243 26.778 7.80457 23.7768C8.0467 20.7756 9.07457 17.891 10.7849 15.413C12.4952 12.9349 14.8276 10.9507 17.5478 9.65985C20.268 8.36895 23.28 7.81683 26.2812 8.05896C29.2824 8.3011 32.167 9.32897 34.645 11.0393C37.123 12.7496 39.1072 15.082 40.3981 17.8022C41.6245 20.3865 42.1841 23.2343 42.0293 26.0859C42.0158 26.3344 42.2001 26.5518 42.4483 26.5718V26.5718Z" fill="#78BE20"/>
       <path class="spinner-inner" d="M24.5807 4.79957C24.5755 4.54573 24.3653 4.34369 24.1117 4.35461C20.6458 4.50374 17.2705 5.52325 14.2986 7.32233C11.1956 9.20083 8.64162 11.8631 6.89352 15.0414C5.14543 18.2198 4.26477 21.8023 4.34006 25.4289C4.41217 28.9022 5.35866 32.2987 7.08874 35.3056C7.21536 35.5256 7.49851 35.5949 7.7157 35.4634V35.4634C7.9329 35.3319 8.00187 35.0495 7.87548 34.8293C6.22886 31.9606 5.32804 28.7217 5.25929 25.4098C5.18735 21.9446 6.02882 18.5215 7.69914 15.4845C9.36946 12.4476 11.8098 9.90379 14.7748 8.10887C17.6086 6.39337 20.8263 5.41969 24.1308 5.27407C24.3844 5.2629 24.586 5.05341 24.5807 4.79957V4.79957Z" fill="#4F99DC"/>
       <g class="spinner-logo" clip-path="url(#clip0_2457_6002)">
       <path d="M28.9827 21.7969L28.6812 20.4565L34.6832 20.4927L34.6751 21.8311L28.9827 21.7969Z" fill="white"/>
       <path d="M29.9236 25.9722L29.6221 24.6318L34.658 24.6622L34.65 26.0006L29.9236 25.9722Z" fill="white"/>
       <path d="M32.1766 18.4167C32.9569 18.4214 33.5849 19.0293 33.5804 19.7755C33.5759 20.5217 32.9406 21.122 32.1603 21.1173C31.38 21.1126 30.752 20.5047 30.7565 19.7585C30.761 19.0123 31.3963 18.412 32.1766 18.4167Z" fill="#50B443"/>
       <path d="M26.6525 22.5317L26.5361 22.531L24.4287 29.4146L22.7517 29.4045L20.6976 22.4959L20.596 22.4953L18.8733 30.0729L17.3846 30.0639L19.697 20.4027L21.431 20.4132L23.5406 27.559L23.6422 27.5597L25.8526 20.4398L27.5866 20.4502L29.7676 30.1385L28.3086 30.1297L26.6501 22.5317L26.6525 22.5317Z" fill="white"/>
       <path d="M28.301 30.1319L30.8623 30.1473L30.5608 28.807L34.6331 28.8315L34.6251 30.17L28.301 30.1319Z" fill="white"/>
       <path d="M17.1287 26.0256C17.909 26.0303 18.5371 26.6382 18.5326 27.3844C18.5281 28.1306 17.8928 28.7309 17.1125 28.7262C16.3322 28.7215 15.7042 28.1136 15.7087 27.3674C15.7132 26.6212 16.3485 26.0209 17.1287 26.0256Z" fill="#50B443"/>
       </g>
      <defs>
      <clipPath id="clip0_2457_6002">
      <rect width="18.9328" height="11.7383" fill="white" transform="translate(15.7632 18.3179) rotate(0.345054)"/>
      </clipPath>
      </defs>
      </svg>
      `
  };

  // Add text to config if provided
  if (text !== null) {
    config.text = text;
  }

  return ElLoading.service(config);
}

/**
 * Converts a numeric column index (1-based) to an Excel column letter.
 * 
 * @param {number} columnIndex - The 1-based column index (1 = A, 2 = B, etc.)
 * @returns {string} - The Excel column letter (A, B, C, ..., Z, AA, AB, etc.)
 */
export function columnIndexToLetter(columnIndex) {
  let result = '';
  while (columnIndex > 0) {
    columnIndex--;
    result = String.fromCharCode(65 + (columnIndex % 26)) + result;
    columnIndex = Math.floor(columnIndex / 26);
  }
  return result;
}

/**
 * Build the country → elec_region tree from get_elec_region_list.
 * Uses API strings as-is. Does not prepend All/N/A.
 */
export function buildElecRegionTreeData(groupedByCountry = {}) {
  return Object.keys(groupedByCountry).map((country) => ({
    value: `country:${country}`,
    label: country,
    children: (groupedByCountry[country] || []).map((region) => ({
      value: `elec_region:${country}:${region}`,
      label: region,
      children: [],
    })),
  }));
}

export function firstElecRegionForCountry(groupedByCountry, country) {
  const regions = groupedByCountry?.[country];
  if (Array.isArray(regions) && regions.length > 0) {
    return regions[0];
  }
  return "";
}

/**
 * Map a tree-select value to country + elec_region.
 * Selecting a country resets elec_region to that country's first option (All).
 */
export function applyTreeLocationSelection(value, groupedByCountry = {}) {
  if (!value) {
    return { country: "", elec_region: "", locationSelection: "" };
  }
  if (value.startsWith("country:")) {
    const country = value.slice("country:".length);
    const elec_region = firstElecRegionForCountry(groupedByCountry, country);
    return {
      country,
      elec_region,
      locationSelection: elec_region
        ? `elec_region:${country}:${elec_region}`
        : value,
    };
  }
  if (value.startsWith("elec_region:")) {
    const rest = value.slice("elec_region:".length);
    const sep = rest.indexOf(":");
    if (sep > -1) {
      return {
        country: rest.slice(0, sep),
        elec_region: rest.slice(sep + 1),
        locationSelection: value,
      };
    }
  }
  return { country: "", elec_region: "", locationSelection: "" };
}

/** Display-only: prefer elec_region on payloads that still use old `state` keys. */
export function locationGroupDisplayValue(item) {
  if (item == null) return "";
  if (typeof item === "string") return item;
  return item.elec_region
    || item.state
    || item.charging_site__elec_region
    || item.charging_site__state
    || "";
}

export function isAuditorUser(user) {
  return user != null && Boolean(user.is_auditor);
}

const AUDITOR_HIDDEN_CUSTOMER_IDS = new Set([2, 10]);

/** Auditors do not see customers 2 and 10 in filter dropdowns. */
export function customersForAccess(customers, user) {
  const list = Array.isArray(customers) ? customers : [];
  if (!isAuditorUser(user)) {
    return list;
  }
  return list.filter((customer) => customer && !AUDITOR_HIDDEN_CUSTOMER_IDS.has(Number(customer.id)));
}

/** Auditors only see monitoring periods whose status is current. */
export function monitoringPeriodsForAccess(periods, user) {
  const list = Array.isArray(periods) ? periods : [];
  if (!isAuditorUser(user)) {
    return list;
  }
  return list.filter((period) => period && period.status === "current");
}

function startOfCalendarDay(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  date.setHours(0, 0, 0, 0);
  return date;
}

/** Inclusive calendar range covering every visible monitoring period. */
export function monitoringPeriodDateBounds(periods) {
  const starts = [];
  const ends = [];
  (periods || []).forEach((period) => {
    const start = startOfCalendarDay(period && period.start_date);
    const end = startOfCalendarDay(period && period.end_date);
    if (start) {
      starts.push(start.getTime());
    }
    if (end) {
      ends.push(end.getTime());
    }
  });
  if (starts.length === 0 || ends.length === 0) {
    return null;
  }
  return {
    start: new Date(Math.min(...starts)),
    end: new Date(Math.max(...ends)),
  };
}

export function isDateOutsideMonitoringPeriodBounds(date, bounds) {
  if (!bounds) {
    return true;
  }
  const day = startOfCalendarDay(date);
  if (!day) {
    return true;
  }
  return day.getTime() < bounds.start.getTime() || day.getTime() > bounds.end.getTime();
}

/** Earliest start and latest end of the visible monitoring periods, as picker values. */
export function monitoringPeriodRangePickerValue(periods) {
  let earliest = null;
  let latest = null;
  (periods || []).forEach((period) => {
    if (!period) {
      return;
    }
    const start = new Date(period.start_date);
    const end = new Date(period.end_date);
    if (!Number.isNaN(start.getTime()) && (earliest == null || start < earliest.time)) {
      earliest = { time: start, value: period.start_date };
    }
    if (!Number.isNaN(end.getTime()) && (latest == null || end > latest.time)) {
      latest = { time: end, value: period.end_date };
    }
  });
  if (!earliest || !latest) {
    return null;
  }
  const toPicker = (value) => String(value).replace("T", " ").slice(0, 19);
  return [toPicker(earliest.value), toPicker(latest.value)];
}

/** Format an ISO-8601 timestamp in the user's local timezone as YYYY-MM-DD HH:mm:ss. */
export function formatIsoTimestamp(value) {
  if (value == null || value === "") {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}